import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import cn from 'classnames';
import debounce from 'lodash.debounce';

import { useLayoutEffect } from '@hooks/useIsomorphicLayoutEffect';
import type { ColourPickerColour } from './ColourPickerContext';
import { ColourPicker } from './ColourPicker';
import styles from './ColourPicker.module.scss';
import type { Swatch } from '@hooks/useHitSwatches';
import { useEventListener } from '@hooks/useEventListener';
import OutsideClickHandler from 'react-outside-click-handler';
import Icon from '@components/ui/Icons';
import { useTranslation } from 'next-i18next';

interface PlpColourPickerProps {
  swatches: (Swatch & { hexColours?: string[] })[];
  /**
   * The `sizes` string of the tile `next/image` that will actually update when a
   * swatch is selected (e.g. the PLP grid tile or product-strip card image) —
   * must match that image's own `sizes` exactly, or hover-preload warms the wrong
   * `next/image` optimizer cache key and the perceived delay returns.
   */
  preloadImageSizes: string;
}

type SwatchWithOptionalHex = Swatch & { hexColours?: string[] };

const mapSwatchesToColourPickerColours = (
  swatches: SwatchWithOptionalHex[]
): ColourPickerColour[] =>
  swatches.map((s, index) => ({
    id: index + 1,
    name: s.swatchName,
    swatchImage: s.swatch || null,
    productImage: s.imageTransparent || s.image || null,
    hexColours: s.hexColours,
    isSelected: s.isActive,
    isDisabled: false,
    isDeal: false, // Intentional: deal treatment is suppressed on PLP swatches
  }));

const PlpColourPicker: React.FC<PlpColourPickerProps> = ({
  swatches,
  preloadImageSizes,
}) => {
  const { t } = useTranslation('pdp');
  const [hasOverflow, setHasOverflow] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [rowHeight, setRowHeight] = useState<number | null>(null);
  const [firstRowCount, setFirstRowCount] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const colours = useMemo(
    () => mapSwatchesToColourPickerColours(swatches),
    [swatches]
  );

  const handleSelectColour = useCallback(
    (id: number) => {
      const index = id - 1;
      swatches[index]?.onClick?.();
    },
    [swatches]
  );

  const measureOverflow = useCallback(() => {
    if (!containerRef.current) return;

    const el = containerRef.current;
    const list = el.querySelector('ul');
    if (!list) return;

    const items = list.querySelectorAll('li');
    /* Single (or no) swatch can never overflow: always clear stale state, even if expanded. */
    if (items.length <= 1) {
      setHasOverflow(false);
      setIsExpanded(false);
      setRowHeight(null);
      setFirstRowCount(null);
      return;
    }

    const firstRect = items[0].getBoundingClientRect();
    const containerRect = el.getBoundingClientRect();
    let firstWrappedIndex = -1;
    for (let i = 1; i < items.length; i++) {
      const rect = items[i].getBoundingClientRect();
      if (rect.top > firstRect.top) {
        firstWrappedIndex = i;
        break;
      }
    }

    if (firstWrappedIndex >= 0) {
      const wrappedRect = items[firstWrappedIndex].getBoundingClientRect();
      const oneRowHeight = wrappedRect.top - containerRect.top;
      setHasOverflow(true);
      setRowHeight(oneRowHeight);
      setFirstRowCount(firstWrappedIndex);
    } else {
      setHasOverflow(false);
      setIsExpanded(false);
      setRowHeight(null);
      setFirstRowCount(null);
    }
  }, [isExpanded]);

  /* Resize: debounce — many events per drag; layout reads + setState are expensive. */
  const debouncedMeasureOverflow = useMemo(
    () => debounce(measureOverflow, 200),
    [measureOverflow]
  );

  /*
   * After DOM commit: measure before paint on the client (useIsomorphicLayoutEffect).
   * On the server this is useEffect so React doesn’t warn; first paint may match SSR,
   * then client layout runs immediately after hydration.
   * Resize: debouncedMeasureOverflow only.
   */
  useLayoutEffect(() => {
    measureOverflow();
  }, [measureOverflow, colours.length, isExpanded]);

  useEffect(() => {
    return () => {
      debouncedMeasureOverflow.cancel?.();
    };
  }, [debouncedMeasureOverflow]);

  useEventListener('resize', debouncedMeasureOverflow);

  const contextValue = useMemo(
    () => ({
      state: { colours },
      actions: { onSelectColour: handleSelectColour },
      variant: 'card' as const,
      overflowCollapsedVisibleCount:
        hasOverflow && !isExpanded && firstRowCount !== null
          ? firstRowCount
          : null,
      preloadImageSizes,
    }),
    [
      colours,
      handleSelectColour,
      hasOverflow,
      isExpanded,
      firstRowCount,
      preloadImageSizes,
    ]
  );

  if (colours.length === 0) return null;

  const handleToggle = () => {
    setIsExpanded((prev) => !prev);
  };

  const moreLabel = t('product.colour.showMore', 'Show more colours');
  const fewerLabel = t('product.colour.showLess', 'Show fewer colours');

  return (
    <div className={styles.rootCard}>
      <ColourPicker.Provider value={contextValue}>
        <div className={styles.swatchOuter}>
          <OutsideClickHandler onOutsideClick={() => setIsExpanded(false)}>
            <div
              ref={containerRef}
              className={cn(styles.swatchOverflowContainer, {
                [styles.swatchOverflowContainerExpanded]: isExpanded,
              })}
              style={
                hasOverflow && !isExpanded && rowHeight
                  ? { maxHeight: rowHeight }
                  : undefined
              }
            >
              <div className={styles.swatchOverflowInner}>
                <div
                  role="radiogroup"
                  aria-label={t('product.colour', 'Colour').replace(
                    /:+\s*$/,
                    ''
                  )}
                >
                  <ColourPicker.Grid />
                </div>
              </div>
              {hasOverflow && (
                <button
                  type="button"
                  className={styles.overflowToggle}
                  onClick={handleToggle}
                  aria-expanded={isExpanded}
                  aria-label={isExpanded ? fewerLabel : moreLabel}
                  title={isExpanded ? fewerLabel : moreLabel}
                >
                  <Icon
                    icon={isExpanded ? 'caret-up' : 'caret-down'}
                    height={16}
                    width={16}
                  />
                </button>
              )}
            </div>
          </OutsideClickHandler>
        </div>
      </ColourPicker.Provider>
    </div>
  );
};

export default PlpColourPicker;
